// Public Our Doctors page. Cards mirror the About → Team design exactly
// for visual consistency: same border, hover lift, avatar with sage ring,
// uppercase sage role label, 2-line bio clamp with a subtle "Read more →"
// link, and a cream-header bio dialog.
//
// Data is admin-curated at /admin/settings/doctors. The featured doctor
// (Lab Head) sorts first by virtue of `is_featured` + sort_order.

import { useState } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import CloseIcon from '@mui/icons-material/Close';
import Eyebrow from '../components/common/Eyebrow';
import Reveal from '../components/common/Reveal';
import { useListDoctorsQuery } from '../store/siteContentApi';

const initialsOf = (name) =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');

// Same circular avatar treatment used on the leadership team — solid sage
// background with the doctor's photo as backgroundImage, plus a 1.5px
// sage-pale outer ring rendered via ::after offset by -5px.
const Avatar = ({ src, name, size = 80 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: 'var(--sage)',
      color: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size > 90 ? 26 : 22,
      fontWeight: 700,
      mx: 'auto',
      mb: 2.5,
      position: 'relative',
      overflow: 'hidden',
      backgroundImage: src ? `url(${src})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: -5,
        borderRadius: '50%',
        border: '1.5px solid var(--sage-p)',
      },
    }}
    aria-label={name}
  >
    {!src && initialsOf(name)}
  </Box>
);

// Featured "Lab Head" hero — full-width card with a larger avatar so the
// senior-most doctor reads as distinct from the grid below. Same visual
// language as DoctorCard (white bg, sage ring, uppercase label) so it
// still feels part of the same family — just dialed up.
const FeaturedDoctorCard = ({ doctor, onReadMore }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1.5px solid var(--border-l)',
      borderRadius: '20px',
      px: { xs: 3, md: 6 },
      py: { xs: 4, md: 5 },
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s',
      // Soft cream wash at the top so the featured tile feels elevated
      // without a heavy shadow. Looks good both as a single full-width
      // card and on smaller widths.
      background: 'linear-gradient(180deg, var(--cream) 0%, #fff 60%)',
      '&:hover': {
        borderColor: 'var(--sage-p)',
        boxShadow: '0 16px 40px rgba(61,122,82,0.10)',
      },
    }}
  >
    {/* Subtle "Lab Head" pill so the visual hierarchy reads even when
        the doctor's title is something else (e.g. "Senior Pathologist"). */}
    <Box
      sx={{
        display: 'inline-block',
        bgcolor: 'rgba(106,174,128,0.15)',
        color: 'var(--sage)',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        borderRadius: 100,
        px: 1.5,
        py: 0.5,
        mb: 2,
      }}
    >
      ★ Head of the Lab
    </Box>

    <Avatar src={doctor.photo_url} name={doctor.name} size={128} />

    <Typography
      className="serif"
      sx={{
        fontSize: { xs: 22, md: 26 },
        fontWeight: 600,
        color: 'var(--ink)',
        mb: 0.5,
        lineHeight: 1.2,
      }}
    >
      {doctor.name}
    </Typography>

    <Typography
      sx={{
        fontSize: 12,
        color: 'var(--sage)',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        mb: doctor.qualifications ? 0.75 : 1.5,
      }}
    >
      {doctor.title}
    </Typography>

    {doctor.qualifications && (
      <Typography
        sx={{
          fontSize: 13,
          color: 'var(--charcoal)',
          fontWeight: 400,
          mb: 1.5,
          lineHeight: 1.55,
        }}
      >
        {doctor.qualifications}
      </Typography>
    )}

    {doctor.bio && (
      <Box sx={{ mt: 2 }}>
        <Button
          onClick={() => onReadMore(doctor)}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--sage)',
            borderColor: 'var(--sage-p)',
            borderRadius: 100,
            px: 2.75,
            py: 0.75,
            '&:hover': {
              bgcolor: 'rgba(106,174,128,0.08)',
              borderColor: 'var(--sage)',
            },
          }}
        >
          Read full bio →
        </Button>
      </Box>
    )}
  </Box>
);

// Same card scaffold as the team tile on /about — copy-paste layout/styles
// so visual rhythm carries between pages.
const DoctorCard = ({ doctor, onReadMore }) => {
  const isLong = (doctor.bio || '').length > 140;
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1.5px solid var(--border-l)',
        borderRadius: '18px',
        p: 3,
        height: '100%',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.25s',
        '&:hover': {
          borderColor: 'var(--sage-p)',
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 32px rgba(61,122,82,0.1)',
        },
      }}
    >
      <Avatar src={doctor.photo_url} name={doctor.name} />

      <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', mb: 0.5 }}>
        {doctor.name}
      </Typography>

      <Typography
        sx={{
          fontSize: 11,
          color: 'var(--sage)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          mb: doctor.qualifications ? 0.75 : 1.5,
        }}
      >
        {doctor.title}
      </Typography>

      {doctor.qualifications && (
        <Typography
          sx={{
            fontSize: 12,
            color: 'var(--charcoal)',
            fontWeight: 400,
            mb: 1.5,
            lineHeight: 1.45,
          }}
        >
          {doctor.qualifications}
        </Typography>
      )}

      {doctor.bio && (
        <Typography
          sx={{
            fontSize: 12.5,
            color: 'var(--stone)',
            lineHeight: 1.65,
            fontWeight: 300,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {doctor.bio}
        </Typography>
      )}

      {/* "Read more" pinned to the bottom via mt: auto so cards in the
          same row line up regardless of bio length. */}
      {isLong && (
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Button
            onClick={() => onReadMore(doctor)}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--sage)',
              letterSpacing: '0.04em',
              px: 0,
              minWidth: 0,
              '&:hover': {
                bgcolor: 'transparent',
                color: 'var(--sage-p)',
              },
            }}
          >
            Read more →
          </Button>
        </Box>
      )}
    </Box>
  );
};

// Cream-header bio dialog — copies the leadership TeamBioDialog so the
// expansion feels like the card opening up, not a separate surface.
const DoctorBioDialog = ({ doctor, onClose }) => {
  const open = !!doctor;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '20px', bgcolor: '#fff', overflow: 'hidden' } }}
    >
      <Box
        sx={{
          position: 'relative',
          px: { xs: 3, md: 4 },
          pt: 4,
          pb: 3,
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--cream) 0%, #fff 100%)',
          borderBottom: '1px solid var(--border-l)',
        }}
      >
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            top: 12, right: 12,
            color: 'var(--stone)',
            '&:hover': { color: 'var(--ink)', bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {doctor && (
          <>
            <Avatar src={doctor.photo_url} name={doctor.name} size={96} />
            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 22, md: 26 },
                fontWeight: 600,
                color: 'var(--ink)',
                lineHeight: 1.2,
                mb: 0.75,
              }}
            >
              {doctor.name}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: 'var(--sage)',
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: doctor.qualifications ? 0.5 : 0,
              }}
            >
              {doctor.title}
            </Typography>
            {doctor.qualifications && (
              <Typography sx={{ fontSize: 12.5, color: 'var(--charcoal)' }}>
                {doctor.qualifications}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ px: { xs: 3, md: 4 }, py: 3.5, maxHeight: '60vh', overflowY: 'auto' }}>
        <Typography
          sx={{
            fontFamily: "'Outfit', sans-serif !important",
            fontSize: { xs: 15, md: 16 },
            color: 'var(--ink)',
            fontWeight: 400,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
          }}
        >
          {doctor?.bio}
        </Typography>
      </Box>
    </Dialog>
  );
};

const OurDoctors = () => {
  const { data: doctors = [], isLoading } = useListDoctorsQuery();
  const [bioOf, setBioOf] = useState(null);

  // Featured doctor (Lab Head) renders in a dedicated full-width card
  // above the grid; everyone else falls into the 4-up tiles. If multiple
  // are flagged featured we still only highlight one — the one that
  // sorts earliest — so the page keeps a single hero.
  const sorted = [...doctors].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const featured = sorted.find((d) => d.is_featured) || null;
  const others = featured ? sorted.filter((d) => d._id !== featured._id) : sorted;

  return (
    <Box>
      {/* ── Hero ── */}
      <Box
        sx={{
          bgcolor: 'var(--forest)',
          pt: { xs: 10, md: 12 },
          pb: { xs: 5, md: 8 },
          px: { xs: 2.5, md: 7 },
        }}
      >
        <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
          <Eyebrow white>Our Doctors</Eyebrow>
          <Typography
            className="serif"
            variant="h1"
            sx={{ color: '#fff', mt: 1.5, mb: 1.5 }}
          >
            Meet the team behind every report
          </Typography>
          <Typography sx={{ fontSize: 16, color: 'rgba(255,255,255,0.55)', fontWeight: 300, maxWidth: 720 }}>
            NABL-accredited pathologists and specialists who personally verify every result before it reaches you.
          </Typography>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Container maxWidth="xl" sx={{ maxWidth: 1300, py: { xs: 5, md: 8 }, px: { xs: 2, md: 4 } }}>
        {isLoading ? (
          <>
            <Skeleton variant="rounded" height={320} sx={{ mb: { xs: 3, md: 4 }, borderRadius: '20px' }} />
            <Grid container spacing={{ xs: 2, md: 2.5 }}>
              {[...Array(8)].map((_, i) => (
                <Grid key={i} item xs={12} sm={6} md={3}>
                  <Skeleton variant="rounded" height={280} sx={{ borderRadius: '18px' }} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : sorted.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center', py: 8,
              border: '1.5px solid var(--border-l)',
              borderRadius: '18px', bgcolor: '#fff',
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', mb: 1 }}>
              Coming soon
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'var(--charcoal)' }}>
              Our doctor profiles will be published shortly.
            </Typography>
          </Box>
        ) : (
          <>
            {featured && (
              <Box sx={{ mb: { xs: 3, md: 4 } }}>
                <Reveal direction="up">
                  <FeaturedDoctorCard doctor={featured} onReadMore={setBioOf} />
                </Reveal>
              </Box>
            )}
            {others.length > 0 && (
              <Grid container spacing={{ xs: 2, md: 2.5 }}>
                {others.map((d, i) => (
                  <Grid key={d._id} item xs={12} sm={6} md={3}>
                    <Reveal direction="up" delay={i * 80} sx={{ height: '100%' }}>
                      <DoctorCard doctor={d} onReadMore={setBioOf} />
                    </Reveal>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>

      <DoctorBioDialog doctor={bioOf} onClose={() => setBioOf(null)} />
    </Box>
  );
};

export default OurDoctors;
